import type { LineItem } from "@/hooks/useEstimateCalculations";

export interface Measurement {
  area_sqft?: number;
  pitch?: string;
  layers?: number;
  valley_lf?: number;
  ridge_lf?: number;
  rake_lf?: number;
  eave_lf?: number;
  hip_lf?: number;
  flashing_lf?: number;     // Headwall flashing (horizontal wall above roof)
  wall_step_lf?: number;     // Sidewall step flashing (sloped wall along roof)
  skylight_count?: number;
  chimney_count?: number;
  vent_count?: number;
  pipe_count?: number;
  fan_count?: number;
  waste_factor?: number;
  eave_extension_ft?: number; // How far ice & water extends up from eave (default 3 ft)
}

export interface CalculationConfig {
  source_fields: string[];
  combine_strategy: 'sum' | 'max' | 'individual';
  coverage_specs: {
    type: 'roll' | 'bundle' | 'linear' | 'area' | null;
    width_ft?: number | null;
    length_ft?: number | null;
    coverage_sqft?: number | null;
    coverage_lf?: number | null;
  };
  waste_factor: number;
  charge_method: 'unit' | 'sqft' | 'lf';
  charge_multiplier: number;
}

export interface Product {
  id: string;
  name: string;
  category: string;
  unit: string;
  sku: string;
  specifications?: any;
  tier?: string | null;
  is_core_component?: boolean;
  tier_description?: string | null;
  is_active?: boolean;
  measurement_field?: string | null;
  calculation_config?: CalculationConfig | null;
}

export interface ProductPrice {
  product_id: string;
  price_book_id: string;
  unit_price: number;
}

export interface GeneratedLineItem extends LineItem {
  calculation_source: 'auto' | 'manual' | 'edited';
  calculation_note?: string;
}

/**
 * Calculate shingles needed based on roof area
 */
export function calculateShinglesNeeded(squares: number, wasteFactor: number = 1.10): number {
  return Number((squares * wasteFactor).toFixed(2));
}

/**
 * Calculate underlayment needed (legacy square-based)
 */
export function calculateUnderlaymentNeeded(squares: number, wasteFactor: number = 1.10): number {
  return Number((squares * wasteFactor).toFixed(2));
}

/**
 * Calculate underlayment rolls needed (roll-based)
 * Standard: 10 squares per roll = 1,000 sq ft per roll
 */
export function calculateUnderlaymentRolls(
  areaSqft: number, 
  wasteFactor: number = 1.10, 
  sqftPerRoll: number = 1000
): number {
  const totalSqftNeeded = areaSqft * wasteFactor;
  return Math.ceil(totalSqftNeeded / sqftPerRoll);
}

/**
 * Calculate Ice & Water Shield for VALLEYS ONLY
 * Standard: 36 inches (3 ft) wide centered on valley
 */
export function calculateIceAndWaterForValleys(
  valleyLF: number,
  wasteFactor: number = 1.10,
  rollCoverageSqft: number = 99
): number {
  const coverageWidth = 3; // 36 inches = 3 feet
  const totalSqftNeeded = valleyLF * coverageWidth * wasteFactor;
  return Math.ceil(totalSqftNeeded / rollCoverageSqft);
}

/**
 * Calculate Ice & Water Shield for EAVES ONLY
 * Standard: Extends from edge to 24-36 inches INSIDE exterior wall line
 * Typical: 3 feet up the roof (measured up the slope)
 */
export function calculateIceAndWaterForEaves(
  eaveLF: number,
  extensionFt: number = 3, // How far up roof from edge
  wasteFactor: number = 1.10,
  rollCoverageSqft: number = 99
): number {
  const totalSqftNeeded = eaveLF * extensionFt * wasteFactor;
  return Math.ceil(totalSqftNeeded / rollCoverageSqft);
}

/**
 * Calculate Ice & Water Shield for HEADWALL FLASHING areas
 * Standard: 12 inches (1 ft) wide along horizontal wall/roof intersection
 */
export function calculateIceAndWaterForHeadwalls(
  headwallLF: number,
  wasteFactor: number = 1.10,
  rollCoverageSqft: number = 99
): number {
  const coverageWidth = 1; // 12 inches = 1 foot
  const totalSqftNeeded = headwallLF * coverageWidth * wasteFactor;
  return Math.ceil(totalSqftNeeded / rollCoverageSqft);
}

/**
 * TOTAL Ice & Water Shield needed
 * Combines valleys, eaves, and headwall flashing
 */
export function calculateTotalIceAndWater(
  valleyLF: number,
  eaveLF: number,
  headwallLF: number,
  eaveExtensionFt: number = 3,
  wasteFactor: number = 1.10,
  rollCoverageSqft: number = 99
): {
  rolls: number;
  breakdown: {
    valleys: { sqft: number; rolls: number };
    eaves: { sqft: number; rolls: number };
    headwalls: { sqft: number; rolls: number };
  };
} {
  const valleySqft = valleyLF * 3 * wasteFactor;
  const eaveSqft = eaveLF * eaveExtensionFt * wasteFactor;
  const headwallSqft = headwallLF * 1 * wasteFactor;
  
  const valleyRolls = Math.ceil(valleySqft / rollCoverageSqft);
  const eaveRolls = Math.ceil(eaveSqft / rollCoverageSqft);
  const headwallRolls = Math.ceil(headwallSqft / rollCoverageSqft);
  
  const totalRolls = valleyRolls + eaveRolls + headwallRolls;
  
  return {
    rolls: totalRolls,
    breakdown: {
      valleys: { sqft: Math.ceil(valleySqft), rolls: valleyRolls },
      eaves: { sqft: Math.ceil(eaveSqft), rolls: eaveRolls },
      headwalls: { sqft: Math.ceil(headwallSqft), rolls: headwallRolls }
    }
  };
}

/**
 * Calculate drip edge needed
 */
export function calculateDripEdgeNeeded(eaveLF: number, rakeLF: number): number {
  return Number((eaveLF + rakeLF).toFixed(1));
}

/**
 * Calculate ridge vent needed
 */
export function calculateRidgeVentNeeded(ridgeLF: number): number {
  return Number(ridgeLF.toFixed(1));
}

/**
 * Calculate ridge cap shingles needed
 */
export function calculateRidgeCapNeeded(ridgeLF: number, hipLF: number = 0): number {
  const totalRidge = ridgeLF + hipLF;
  return Number((totalRidge * 1.1).toFixed(1)); // 10% overage
}

/**
 * Calculate valley material needed
 */
export function calculateValleyMaterialNeeded(valleyLF: number): number {
  return Number((valleyLF * 1.1).toFixed(1)); // 10% overage
}

/**
 * Calculate starter shingles needed
 */
export function calculateStarterShinglesNeeded(eaveLF: number, rakeLF: number): number {
  return Number((eaveLF + rakeLF).toFixed(1));
}

/**
 * Calculate squares from square feet
 */
export function calculateSquares(areaSqft: number): number {
  return Number((areaSqft / 100).toFixed(2));
}

/**
 * Calculate gutters needed (equals eave length)
 */
export function calculateGuttersNeeded(eaveLF: number): number {
  return Number(eaveLF.toFixed(1));
}

/**
 * Calculate Step Flashing pieces needed for SIDEWALLS
 * Standard: 8"x8" or 8"x10" pieces installed at each shingle course
 * Typical exposure: 5 inches (so 2.4 pieces per linear foot)
 */
export function calculateStepFlashingPieces(
  sidewallLF: number,
  piecesPerFoot: number = 2.4, // 12" ÷ 5" exposure = 2.4
  wasteFactor: number = 1.10
): number {
  const totalPieces = sidewallLF * piecesPerFoot * wasteFactor;
  return Math.ceil(totalPieces);
}

/**
 * Calculate downspouts needed (manual entry, no auto-calculation)
 * User specifies total linear feet needed
 */
export function calculateDownspoutsNeeded(linearFeet: number): number {
  return Number(linearFeet.toFixed(1));
}

/**
 * Find product by category keyword
 */
function findProductByCategory(
  products: Product[],
  keywords: string[],
  category?: string
): Product | undefined {
  return products.find((p) => {
    const matchesCategory = category ? p.category.toLowerCase() === category.toLowerCase() : true;
    const matchesKeyword = keywords.some(
      (kw) =>
        p.name.toLowerCase().includes(kw.toLowerCase()) ||
        p.sku.toLowerCase().includes(kw.toLowerCase())
    );
    return matchesCategory && matchesKeyword;
  });
}

/**
 * Get price for product from price book
 */
function getProductPrice(
  productId: string,
  priceBookId: string,
  productPrices: ProductPrice[]
): number {
  const price = productPrices.find(
    (pp) => pp.product_id === productId && pp.price_book_id === priceBookId
  );
  return price?.unit_price || 0;
}

/**
 * Main function: Generate all line items from measurements
 */
export function generateLineItemsFromMeasurements(
  measurements: Measurement,
  priceBookId: string,
  products: Product[],
  productPrices: ProductPrice[]
): GeneratedLineItem[] {
  const lineItems: GeneratedLineItem[] = [];
  const wasteFactor = measurements.waste_factor || 1.10;

  const {
    area_sqft = 0,
    valley_lf = 0,
    ridge_lf = 0,
    rake_lf = 0,
    eave_lf = 0,
    hip_lf = 0,
    flashing_lf = 0,
    skylight_count = 0,
    chimney_count = 0,
    vent_count = 0,
    pipe_count = 0,
    fan_count = 0,
  } = measurements;

  const squares = calculateSquares(area_sqft);

  // 1. Shingles
  const shinglesProduct = findProductByCategory(products, ["shingle", "timberline"], "Shingles");
  if (shinglesProduct && area_sqft > 0) {
    const quantity = calculateShinglesNeeded(squares, wasteFactor);
    const unitPrice = getProductPrice(shinglesProduct.id, priceBookId, productPrices);
    lineItems.push({
      product_id: shinglesProduct.id,
      description: shinglesProduct.name,
      quantity,
      unit: shinglesProduct.unit,
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `Based on ${squares} squares × ${wasteFactor} waste factor`,
    });
  }

  // 2. Underlayment (roll-based)
  const underlaymentProduct = findProductByCategory(
    products,
    ["underlayment", "felt", "synthetic"],
    "Underlayment"
  );
  if (underlaymentProduct && area_sqft > 0) {
    const specs = underlaymentProduct.specifications as any;
    const sqftPerRoll = specs?.coverage_per_unit || 1000; // Default 10 squares = 1000 sq ft
    const quantity = calculateUnderlaymentRolls(area_sqft, wasteFactor, sqftPerRoll);
    const unitPrice = getProductPrice(underlaymentProduct.id, priceBookId, productPrices);
    
    const totalSqftNeeded = Math.ceil(area_sqft * wasteFactor);
    lineItems.push({
      product_id: underlaymentProduct.id,
      description: underlaymentProduct.name,
      quantity,
      unit: 'roll',
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `${area_sqft} sq ft × ${wasteFactor} waste = ${totalSqftNeeded} sq ft ÷ ${sqftPerRoll} sq ft/roll`,
    });
  }

  // 3. Ice & Water Shield (PROPER CALCULATION - separated by application)
  const iceWaterProduct = findProductByCategory(
    products,
    ["ice", "water", "shield", "barrier"],
    "Ice & Water"
  );

  if (iceWaterProduct && (valley_lf > 0 || eave_lf > 0 || flashing_lf > 0)) {
    const specs = iceWaterProduct.specifications as any;
    const rollCoverage = specs?.roll_dimensions?.coverage || 99; // 3ft × 33ft = 99 sq ft
    const eaveExtension = measurements.eave_extension_ft || 3; // 3 feet up from eave (standard)
    
    const iceWaterCalc = calculateTotalIceAndWater(
      valley_lf,
      eave_lf,
      flashing_lf, // Using flashing_lf for headwall areas
      eaveExtension,
      wasteFactor,
      rollCoverage
    );
    
    const unitPrice = getProductPrice(iceWaterProduct.id, priceBookId, productPrices);
    
    const breakdown = iceWaterCalc.breakdown;
    const noteLines = [];
    if (valley_lf > 0) noteLines.push(`Valleys: ${valley_lf} LF × 3 ft wide = ${breakdown.valleys.sqft} sq ft (${breakdown.valleys.rolls} rolls)`);
    if (eave_lf > 0) noteLines.push(`Eaves: ${eave_lf} LF × ${eaveExtension} ft up = ${breakdown.eaves.sqft} sq ft (${breakdown.eaves.rolls} rolls)`);
    if (flashing_lf > 0) noteLines.push(`Headwalls: ${flashing_lf} LF × 1 ft wide = ${breakdown.headwalls.sqft} sq ft (${breakdown.headwalls.rolls} rolls)`);
    
    lineItems.push({
      product_id: iceWaterProduct.id,
      description: iceWaterProduct.name,
      quantity: iceWaterCalc.rolls,
      unit: 'roll',
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: iceWaterCalc.rolls * unitPrice,
      calculation_source: 'auto',
      calculation_note: noteLines.join(' | '),
    });
  }

  // 4. Drip Edge
  if (eave_lf > 0 || rake_lf > 0) {
    const dripEdgeProduct = findProductByCategory(products, ["drip", "edge"], "Drip Edge");
    if (dripEdgeProduct) {
      const quantity = calculateDripEdgeNeeded(eave_lf, rake_lf);
      const unitPrice = getProductPrice(dripEdgeProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: dripEdgeProduct.id,
        description: dripEdgeProduct.name,
        quantity,
        unit: dripEdgeProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Eave: ${eave_lf} LF + Rake: ${rake_lf} LF`,
      });
    }
  }

  // 5. Ridge Vent
  if (ridge_lf > 0) {
    const ridgeVentProduct = findProductByCategory(
      products,
      ["ridge", "vent"],
      "Ventilation"
    );
    if (ridgeVentProduct) {
      const quantity = calculateRidgeVentNeeded(ridge_lf);
      const unitPrice = getProductPrice(ridgeVentProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: ridgeVentProduct.id,
        description: ridgeVentProduct.name,
        quantity,
        unit: ridgeVentProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Ridge: ${ridge_lf} LF`,
      });
    }
  }

  // 6. Ridge Cap
  if (ridge_lf > 0 || hip_lf > 0) {
    const ridgeCapProduct = findProductByCategory(products, ["ridge", "cap"], "Ridge Cap");
    if (ridgeCapProduct) {
      const quantity = calculateRidgeCapNeeded(ridge_lf, hip_lf);
      const unitPrice = getProductPrice(ridgeCapProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: ridgeCapProduct.id,
        description: ridgeCapProduct.name,
        quantity,
        unit: ridgeCapProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Ridge: ${ridge_lf} LF + Hip: ${hip_lf} LF × 1.1`,
      });
    }
  }

  // 7. Valley Material
  if (valley_lf > 0) {
    const valleyProduct = findProductByCategory(products, ["valley"], "Flashing");
    if (valleyProduct) {
      const quantity = calculateValleyMaterialNeeded(valley_lf);
      const unitPrice = getProductPrice(valleyProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: valleyProduct.id,
        description: valleyProduct.name,
        quantity,
        unit: valleyProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Valley: ${valley_lf} LF × 1.1`,
      });
    }
  }

  // 8. Starter Shingles
  if (eave_lf > 0 || rake_lf > 0) {
    const starterProduct = findProductByCategory(products, ["starter"], "Starter Strip");
    if (starterProduct) {
      const quantity = calculateStarterShinglesNeeded(eave_lf, rake_lf);
      const unitPrice = getProductPrice(starterProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: starterProduct.id,
        description: starterProduct.name,
        quantity,
        unit: starterProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Eave: ${eave_lf} LF + Rake: ${rake_lf} LF`,
      });
    }
  }

  // 9. Pipe Boots
  const totalPipes = (pipe_count || 0) + (vent_count || 0);
  if (totalPipes > 0) {
    const pipeBootProduct = findProductByCategory(products, ["pipe", "boot"], "Flashing");
    if (pipeBootProduct) {
      const unitPrice = getProductPrice(pipeBootProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: pipeBootProduct.id,
        description: pipeBootProduct.name,
        quantity: totalPipes,
        unit: pipeBootProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: totalPipes * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${totalPipes} pipe penetrations`,
      });
    }
  }

  // 9B. Step Flashing (for sidewalls)
  const sidewallLF = measurements.wall_step_lf || 0;
  if (sidewallLF > 0) {
    const stepFlashingProduct = findProductByCategory(
      products,
      ["step", "flashing"],
      "Flashing"
    );
    
    if (stepFlashingProduct) {
      const quantity = calculateStepFlashingPieces(sidewallLF, 2.4, wasteFactor);
      const unitPrice = getProductPrice(stepFlashingProduct.id, priceBookId, productPrices);
      
      lineItems.push({
        product_id: stepFlashingProduct.id,
        description: stepFlashingProduct.name,
        quantity,
        unit: stepFlashingProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Sidewall: ${sidewallLF} LF × 2.4 pieces/ft = ${quantity} pieces (8"x8" at 5" exposure)`,
      });
    }
  }

  // 10. Skylight Flashing
  if (skylight_count > 0) {
    const skylightFlashingProduct = findProductByCategory(
      products,
      ["skylight", "flashing"],
      "Flashing"
    );
    if (skylightFlashingProduct) {
      const unitPrice = getProductPrice(skylightFlashingProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: skylightFlashingProduct.id,
        description: skylightFlashingProduct.name,
        quantity: skylight_count,
        unit: skylightFlashingProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: skylight_count * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${skylight_count} skylights`,
      });
    }
  }

  // 11. Chimney Flashing
  if (chimney_count > 0) {
    const chimneyFlashingProduct = findProductByCategory(
      products,
      ["chimney", "flashing"],
      "Flashing"
    );
    if (chimneyFlashingProduct) {
      const unitPrice = getProductPrice(chimneyFlashingProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: chimneyFlashingProduct.id,
        description: chimneyFlashingProduct.name,
        quantity: chimney_count,
        unit: chimneyFlashingProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: chimney_count * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${chimney_count} chimneys`,
      });
    }
  }

  // 12. Roof Fans
  if (fan_count > 0) {
    const fanProduct = findProductByCategory(products, ["fan", "vent"], "Ventilation");
    if (fanProduct) {
      const unitPrice = getProductPrice(fanProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: fanProduct.id,
        description: fanProduct.name,
        quantity: fan_count,
        unit: fanProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: fan_count * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${fan_count} roof fans`,
      });
    }
  }

  // 13. Gutters (K-Style 5")
  if (eave_lf > 0) {
    const gutterProduct = findProductByCategory(products, ["k-style", "gutter"], "Gutters");
    if (gutterProduct) {
      const quantity = calculateGuttersNeeded(eave_lf);
      const unitPrice = getProductPrice(gutterProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: gutterProduct.id,
        description: gutterProduct.name,
        quantity,
        unit: gutterProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Eaves: ${eave_lf} LF (gutters match eave length)`,
      });
    }
  }

  // Note: Downspouts are manual entry only, not auto-calculated
  // User adds downspouts manually and specifies total linear feet needed

  return lineItems;
}

/**
 * Calculate product quantity using the new calculation_config system
 * This is the new, flexible calculation method that replaces hardcoded logic
 */
export function calculateProductQuantity(
  product: Product,
  measurements: Measurement,
  priceBookId: string,
  productPrices: ProductPrice[]
): {
  quantity: number;
  unit: string;
  calculation_note: string;
  unit_price: number;
  line_total: number;
} {
  const config = product.calculation_config;
  
  // If no calculation config, fall back to legacy measurement_field
  if (!config || config.source_fields.length === 0) {
    const legacyQuantity = calculateQuantityFromMeasurementField(
      product.measurement_field,
      measurements,
      product.category
    );
    const price = getProductPrice(product.id, priceBookId, productPrices);
    return {
      quantity: legacyQuantity,
      unit: product.unit,
      calculation_note: product.measurement_field 
        ? `Legacy formula: ${product.measurement_field}` 
        : 'Manual entry (no calculation configured)',
      unit_price: price,
      line_total: Number((legacyQuantity * price).toFixed(2))
    };
  }

  // 1. Get measurement values from source fields
  const sourceValues = config.source_fields.map(field => {
    const value = measurements[field as keyof Measurement];
    return typeof value === 'number' ? value : 0;
  });

  // 2. Combine values based on strategy
  let totalMeasurement = 0;
  switch (config.combine_strategy) {
    case 'sum':
      totalMeasurement = sourceValues.reduce((a, b) => a + b, 0);
      break;
    case 'max':
      totalMeasurement = Math.max(...sourceValues, 0);
      break;
    case 'individual':
      totalMeasurement = sourceValues[0] || 0;
      break;
  }

  // 3. Apply coverage specs and waste
  const wasteFactor = config.waste_factor || 1.10;
  const specs = config.coverage_specs;
  
  let quantity = 0;
  let calculationSteps: string[] = [];

  if (specs.type === 'roll' && specs.coverage_sqft) {
    // Roll-based calculation
    const totalSqftNeeded = totalMeasurement * wasteFactor;
    quantity = Math.ceil(totalSqftNeeded / specs.coverage_sqft);
    calculationSteps = [
      `${config.source_fields.join('+')}=${totalMeasurement.toFixed(1)}`,
      `×${wasteFactor}=${totalSqftNeeded.toFixed(1)} sqft`,
      `÷${specs.coverage_sqft} sqft/roll=${quantity} rolls`
    ];
  } else if (specs.type === 'bundle' && specs.coverage_sqft) {
    // Bundle-based calculation
    const squares = totalMeasurement / 100;
    const squaresWithWaste = squares * wasteFactor;
    const coveragePerBundle = specs.coverage_sqft / 100;
    quantity = Math.ceil(squaresWithWaste / coveragePerBundle);
    calculationSteps = [
      `${totalMeasurement} sqft = ${squares.toFixed(2)} squares`,
      `×${wasteFactor}=${squaresWithWaste.toFixed(2)} squares`,
      `÷${coveragePerBundle.toFixed(2)} squares/bundle=${quantity} bundles`
    ];
  } else if (specs.type === 'linear') {
    // Linear calculation (no coverage conversion needed)
    quantity = Math.ceil(totalMeasurement * wasteFactor);
    calculationSteps = [
      `${config.source_fields.join('+')}=${totalMeasurement.toFixed(1)} LF`,
      `×${wasteFactor}=${quantity} LF`
    ];
  } else if (specs.type === 'area' && specs.coverage_sqft) {
    // Area-based (like underlayment)
    const totalSqftNeeded = totalMeasurement * wasteFactor;
    quantity = Math.ceil(totalSqftNeeded / specs.coverage_sqft);
    calculationSteps = [
      `${totalMeasurement} sqft`,
      `×${wasteFactor}=${totalSqftNeeded.toFixed(1)} sqft`,
      `÷${specs.coverage_sqft} sqft/unit=${quantity} units`
    ];
  } else {
    // No coverage specs - just apply waste to raw measurement
    quantity = Math.ceil(totalMeasurement * wasteFactor);
    calculationSteps = [
      `${config.source_fields.join('+')}=${totalMeasurement.toFixed(1)}`,
      `×${wasteFactor}=${quantity}`
    ];
  }

  // 4. Adjust for charge method
  let finalQuantity = quantity;
  let finalUnit = product.unit;
  
  if (config.charge_method === 'sqft' && specs.coverage_sqft) {
    // Charging by sqft instead of units
    finalQuantity = Math.ceil(totalMeasurement * wasteFactor);
    finalUnit = 'sf';
    calculationSteps.push(`Billing by sqft: ${finalQuantity} sf`);
  } else if (config.charge_method === 'lf') {
    // Charging by linear ft
    finalQuantity = Math.ceil(totalMeasurement * wasteFactor);
    finalUnit = 'lf';
    calculationSteps.push(`Billing by LF: ${finalQuantity} lf`);
  }

  // 5. Get price and calculate total
  const unitPrice = getProductPrice(product.id, priceBookId, productPrices);
  const lineTotal = Number((finalQuantity * unitPrice).toFixed(2));

  return {
    quantity: finalQuantity,
    unit: finalUnit,
    calculation_note: calculationSteps.join(' → '),
    unit_price: unitPrice,
    line_total: lineTotal
  };
}

/**
 * Validate measurements are sufficient for generating line items
 */
export function validateMeasurements(measurements: Measurement): {
  valid: boolean;
  missing: string[];
} {
  const missing: string[] = [];

  if (!measurements.area_sqft || measurements.area_sqft <= 0) {
    missing.push("Total Area (sq ft)");
  }

  return {
    valid: missing.length === 0,
    missing,
  };
}

/**
 * Calculate quantity from a product's measurement_field string
 * Supports simple fields and advanced mathematical formulas
 * 
 * Formula Examples:
 * - Simple field: "area_sqft"
 * - Addition: "eave_lf+rake_lf"
 * - With waste factor: "(eave_lf+rake_lf)*1.1"
 * - Division for bundles: "(ridge_lf+hip_lf)*1.1/16.67"
 * - Area to squares with bundles: "area_sqft/100*1.1*3"
 * - Complex: "(eave_lf*3+valley_lf*3)/225*1.1"
 */
export function calculateQuantityFromMeasurementField(
  measurementField: string | null | undefined,
  measurements: Measurement,
  category: string
): number {
  if (!measurementField) return 0;

  try {
    console.log(`📐 Calculating quantity for formula: "${measurementField}"`);
    console.log(`📊 Available measurements:`, measurements);
    
    let formula = measurementField.trim();
    
    // Define all available measurement fields
    const fieldMap: Record<string, number> = {
      area_sqft: measurements.area_sqft || 0,
      layers: measurements.layers || 0,
      valley_lf: measurements.valley_lf || 0,
      ridge_lf: measurements.ridge_lf || 0,
      rake_lf: measurements.rake_lf || 0,
      eave_lf: measurements.eave_lf || 0,
      hip_lf: measurements.hip_lf || 0,
      flashing_lf: measurements.flashing_lf || 0,
      wall_step_lf: measurements.wall_step_lf || 0,
      skylight_count: measurements.skylight_count || 0,
      chimney_count: measurements.chimney_count || 0,
      vent_count: measurements.vent_count || 0,
      pipe_count: measurements.pipe_count || 0,
      fan_count: measurements.fan_count || 0,
    };

    // Replace all field names in the formula with their numeric values
    Object.entries(fieldMap).forEach(([fieldName, value]) => {
      // Use word boundaries to match whole words only (avoids partial matches)
      const regex = new RegExp(`\\b${fieldName}\\b`, 'g');
      formula = formula.replace(regex, value.toString());
    });

    console.log(`🔢 Formula after substitution: "${formula}"`);

    // Evaluate the mathematical expression
    // Using Function constructor for safer evaluation than eval()
    // eslint-disable-next-line no-new-func
    const result = new Function(`return ${formula}`)();
    const quantity = Number(result);

    if (isNaN(quantity) || !isFinite(quantity)) {
      console.warn(`⚠️ Invalid result from formula "${measurementField}": ${result}`);
      return 0;
    }

    const roundedQuantity = Number(quantity.toFixed(2));
    console.log(`✅ Calculated quantity: ${roundedQuantity}`);

    return roundedQuantity;
  } catch (error) {
    console.error(`❌ Error calculating quantity from formula "${measurementField}":`, error);
    return 0;
  }
}

/**
 * Generate estimate line items from default products only
 * Uses phase settings to determine which products to include
 */
export function generateEstimateFromDefaults(
  measurements: Measurement,
  phaseSettings: Array<{ 
    id: string; 
    phase_key: string;
    default_product_id: string | null;
  }>,
  products: Product[],
  productPrices: ProductPrice[],
  priceBookId: string
): GeneratedLineItem[] {
  const lineItems: GeneratedLineItem[] = [];
  
  console.log('🎯 Generating estimate from default products...');
  console.log('Phase settings:', phaseSettings.length);
  console.log('Products available:', products.length);
  console.log('Measurements:', measurements);
  
  // For each phase that has a default product
  for (const phaseSetting of phaseSettings) {
    if (!phaseSetting.default_product_id) {
      console.log(`⏭️ Skipping ${phaseSetting.phase_key} - no default product set`);
      continue;
    }
    
    const product = products.find(p => p.id === phaseSetting.default_product_id);
    if (!product || !product.is_active) {
      console.warn(`⚠️ Default product not found or inactive for ${phaseSetting.phase_key}`);
      continue;
    }
    
    // Get price for this product
    const price = productPrices.find(pp => 
      pp.product_id === product.id && 
      pp.price_book_id === priceBookId
    );
    
    if (!price) {
      console.warn(`⚠️ No price found for ${product.name}`);
      continue;
    }
    
    // Calculate quantity based on product's measurement_field
    const quantity = calculateQuantityFromMeasurementField(
      product.measurement_field,
      measurements,
      product.category
    );
    
    // Use quantity of 1 if no measurement data or calculation failed
    const finalQuantity = quantity > 0 ? quantity : 1;
    const lineTotal = finalQuantity * price.unit_price;
    
    console.log(`✅ Adding ${product.name}: ${finalQuantity} ${product.unit} @ $${price.unit_price}`);
    
    lineItems.push({
      product_id: product.id,
      description: product.name,
      quantity: finalQuantity,
      unit: product.unit,
      unit_price: price.unit_price,
      discount_percent: 0,
      discount_amount: 0,
      line_total: Number(lineTotal.toFixed(2)),
      calculation_source: 'auto',
      calculation_note: quantity > 0 
        ? `Auto-calculated from ${product.measurement_field || 'measurements'}: ${quantity}` 
        : 'Default quantity (no measurement field configured)'
    });
  }
  
  console.log(`📦 Generated ${lineItems.length} line items from defaults`);
  return lineItems;
}

/**
 * Generate a complete roofing system with all core components
 * Automatically selects products based on tier (good/better/best)
 */
export function generateCompleteRoofSystem(
  measurements: Measurement,
  products: Product[],
  productPrices: ProductPrice[],
  priceBookId: string,
  tier: 'good' | 'better' | 'best' = 'better'
): GeneratedLineItem[] {
  // Validate measurements have data
  if (!measurements.area_sqft || measurements.area_sqft <= 0) {
    console.error('Cannot generate complete roof system: No area measurement provided');
    return [];
  }

  console.log('🏗️ Generating complete roof system:', {
    tier,
    area_sqft: measurements.area_sqft,
    productsAvailable: products.length,
    pricesAvailable: productPrices.length
  });

  const lineItems: GeneratedLineItem[] = [];
  const wasteFactor = measurements.waste_factor || 1.10;

  // Helper to find product by tier and category
  const findTierProduct = (category: string, keywords: string[]): Product | undefined => {
    // First try to find tier-specific core component
    const tierProduct = products.find(
      p => p.category === category && 
           p.tier === tier && 
           p.is_core_component &&
           keywords.some(kw => p.name.toLowerCase().includes(kw.toLowerCase()))
    );
    if (tierProduct) {
      console.log(`✅ Found ${tier} tier product:`, tierProduct.name);
      return tierProduct;
    }

    // NEW: Try to find core component with NULL tier (treat as "better")
    if (tier === 'better') {
      const nullTierProduct = products.find(
        p => p.category === category && 
             p.tier === null && 
             p.is_core_component &&
             keywords.some(kw => p.name.toLowerCase().includes(kw.toLowerCase()))
      );
      if (nullTierProduct) {
        console.log(`⚠️ Using null-tier product as "better":`, nullTierProduct.name);
        return nullTierProduct;
      }
    }

    // Fallback to any core component in category
    const coreProduct = products.find(
      p => p.category === category && 
           p.is_core_component &&
           keywords.some(kw => p.name.toLowerCase().includes(kw.toLowerCase()))
    );
    if (coreProduct) {
      console.log(`⚠️ Using fallback core product:`, coreProduct.name);
      return coreProduct;
    }

    // Final fallback to any product matching keywords
    const fallback = findProductByCategory(products, keywords, category);
    if (fallback) {
      console.log(`⚠️ Using general fallback:`, fallback.name);
    }
    return fallback;
  };

  // Extract measurements
  const squares = calculateSquares(measurements.area_sqft || 0);
  const valley_lf = measurements.valley_lf || 0;
  const ridge_lf = measurements.ridge_lf || 0;
  const rake_lf = measurements.rake_lf || 0;
  const eave_lf = measurements.eave_lf || 0;
  const hip_lf = measurements.hip_lf || 0;
  const flashing_lf = measurements.flashing_lf || 0;
  const sidewall_lf = measurements.wall_step_lf || 0;
  const layers = measurements.layers || 1;
  const eaveExtension = measurements.eave_extension_ft || 3;

  // 1. TEAR-OFF & DISPOSAL
  const tearOffProduct = findTierProduct('Tear-Off', ['tear', 'off', 'removal']);
  if (tearOffProduct && layers > 0) {
    const quantity = squares * layers;
    const unitPrice = getProductPrice(tearOffProduct.id, priceBookId, productPrices);
    lineItems.push({
      product_id: tearOffProduct.id,
      description: tearOffProduct.name,
      quantity,
      unit: tearOffProduct.unit,
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `${layers} layer(s) × ${squares} squares`,
    });
  }

  // 2. DRIP EDGE
  const dripEdgeProduct = findTierProduct('Drip Edge', ['drip', 'edge']);
  if (dripEdgeProduct && (eave_lf > 0 || rake_lf > 0)) {
    const quantity = calculateDripEdgeNeeded(eave_lf, rake_lf);
    const unitPrice = getProductPrice(dripEdgeProduct.id, priceBookId, productPrices);
    lineItems.push({
      product_id: dripEdgeProduct.id,
      description: dripEdgeProduct.name,
      quantity,
      unit: dripEdgeProduct.unit,
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `Eave: ${eave_lf} LF + Rake: ${rake_lf} LF = ${quantity} LF`,
    });
  }

  // 3. ICE & WATER SHIELD (tier-dependent coverage)
  const iceWaterProduct = findTierProduct('Ice & Water', ['ice', 'water', 'shield']);
  if (iceWaterProduct) {
    const specs = iceWaterProduct.specifications as any;
    const rollCoverage = specs?.roll_dimensions?.coverage || 99;
    
    // Best tier gets full coverage including sidewalls
    const includeSidewalls = tier === 'best';
    const totalFlashing = includeSidewalls ? flashing_lf + sidewall_lf : flashing_lf;
    
    const iceWaterCalc = calculateTotalIceAndWater(
      valley_lf,
      eave_lf,
      totalFlashing,
      eaveExtension,
      wasteFactor,
      rollCoverage
    );
    
    const unitPrice = getProductPrice(iceWaterProduct.id, priceBookId, productPrices);
    const noteLines = [];
    if (valley_lf > 0) noteLines.push(`Valleys: ${valley_lf} LF`);
    if (eave_lf > 0) noteLines.push(`Eaves: ${eave_lf} LF`);
    if (flashing_lf > 0) noteLines.push(`Headwalls: ${flashing_lf} LF`);
    if (includeSidewalls && sidewall_lf > 0) noteLines.push(`Sidewalls: ${sidewall_lf} LF`);
    
    lineItems.push({
      product_id: iceWaterProduct.id,
      description: iceWaterProduct.name,
      quantity: iceWaterCalc.rolls,
      unit: 'roll',
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: iceWaterCalc.rolls * unitPrice,
      calculation_source: 'auto',
      calculation_note: noteLines.join(' | ') + ` (${tier} tier coverage)`,
    });
  }

  // 4. UNDERLAYMENT
  const underlaymentProduct = findTierProduct('Underlayment', ['underlayment', 'felt', 'synthetic']);
  if (underlaymentProduct) {
    const specs = underlaymentProduct.specifications as any;
    const sqftPerRoll = specs?.roll_dimensions?.coverage || 1000;
    const quantity = calculateUnderlaymentRolls(measurements.area_sqft || 0, wasteFactor, sqftPerRoll);
    const unitPrice = getProductPrice(underlaymentProduct.id, priceBookId, productPrices);
    
    lineItems.push({
      product_id: underlaymentProduct.id,
      description: underlaymentProduct.name,
      quantity,
      unit: 'roll',
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `${measurements.area_sqft} sq ft ÷ ${sqftPerRoll} sq ft/roll × ${wasteFactor} waste = ${quantity} rolls`,
    });
  }

  // 5. STARTER STRIP
  const starterProduct = findTierProduct('Starter Strip', ['starter']);
  if (starterProduct && (eave_lf > 0 || rake_lf > 0)) {
    const quantity = calculateStarterShinglesNeeded(eave_lf, rake_lf);
    const unitPrice = getProductPrice(starterProduct.id, priceBookId, productPrices);
    
    lineItems.push({
      product_id: starterProduct.id,
      description: starterProduct.name,
      quantity,
      unit: 'LF',
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `Eave: ${eave_lf} LF + Rake: ${rake_lf} LF = ${quantity} LF`,
    });
  }

  // 6. SHINGLES (main roofing material)
  const shinglesProduct = findTierProduct('Shingles', ['shingle', 'timberline', 'royal', 'harvest']);
  if (shinglesProduct) {
    const quantity = calculateShinglesNeeded(squares, wasteFactor);
    const unitPrice = getProductPrice(shinglesProduct.id, priceBookId, productPrices);
    
    lineItems.push({
      product_id: shinglesProduct.id,
      description: shinglesProduct.name,
      quantity,
      unit: shinglesProduct.unit,
      unit_price: unitPrice,
      discount_percent: 0,
      discount_amount: 0,
      line_total: quantity * unitPrice,
      calculation_source: 'auto',
      calculation_note: `${squares} squares × ${wasteFactor} waste = ${quantity} ${shinglesProduct.unit}`,
    });
  }

  // 7. RIDGE VENT
  if (ridge_lf > 0) {
    const ridgeVentProduct = findTierProduct('Ridge Vent', ['ridge', 'vent']);
    if (ridgeVentProduct) {
      const quantity = calculateRidgeVentNeeded(ridge_lf);
      const unitPrice = getProductPrice(ridgeVentProduct.id, priceBookId, productPrices);
      
      lineItems.push({
        product_id: ridgeVentProduct.id,
        description: ridgeVentProduct.name,
        quantity,
        unit: 'LF',
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${ridge_lf} LF ridge length`,
      });
    }
  }

  // 8. RIDGE CAP
  if (ridge_lf > 0 || hip_lf > 0) {
    const ridgeCapProduct = findTierProduct('Ridge Cap', ['ridge', 'cap']);
    if (ridgeCapProduct) {
      const quantity = calculateRidgeCapNeeded(ridge_lf, hip_lf);
      const unitPrice = getProductPrice(ridgeCapProduct.id, priceBookId, productPrices);
      
      lineItems.push({
        product_id: ridgeCapProduct.id,
        description: ridgeCapProduct.name,
        quantity,
        unit: 'LF',
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Ridge: ${ridge_lf} LF + Hip: ${hip_lf} LF = ${quantity} LF`,
      });
    }
  }

  // 9. STEP FLASHING (for sidewalls)
  if (sidewall_lf > 0) {
    const stepFlashingProduct = findTierProduct('Flashing', ['step', 'flashing']);
    if (stepFlashingProduct) {
      const quantity = calculateStepFlashingPieces(sidewall_lf, 2.4, wasteFactor);
      const unitPrice = getProductPrice(stepFlashingProduct.id, priceBookId, productPrices);
      
      lineItems.push({
        product_id: stepFlashingProduct.id,
        description: stepFlashingProduct.name,
        quantity,
        unit: stepFlashingProduct.unit,
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: quantity * unitPrice,
        calculation_source: 'auto',
        calculation_note: `Sidewall: ${sidewall_lf} LF × 2.4 pieces/ft = ${quantity} pieces`,
      });
    }
  }

  // 10. PIPE BOOTS
  const pipeCount = measurements.pipe_count || 0;
  if (pipeCount > 0) {
    const pipeBootProduct = findProductByCategory(products, ['pipe', 'boot'], 'Flashing');
    if (pipeBootProduct) {
      const unitPrice = getProductPrice(pipeBootProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: pipeBootProduct.id,
        description: pipeBootProduct.name,
        quantity: pipeCount,
        unit: 'each',
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: pipeCount * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${pipeCount} pipe penetrations`,
      });
    }
  }

  // 11. VENTS
  const ventCount = measurements.vent_count || 0;
  if (ventCount > 0) {
    const ventProduct = findProductByCategory(products, ['vent', 'turbine'], 'Ventilation');
    if (ventProduct) {
      const unitPrice = getProductPrice(ventProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: ventProduct.id,
        description: ventProduct.name,
        quantity: ventCount,
        unit: 'each',
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: ventCount * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${ventCount} roof vents`,
      });
    }
  }

  // 12. SKYLIGHT FLASHING
  const skylightCount = measurements.skylight_count || 0;
  if (skylightCount > 0) {
    const skylightProduct = findProductByCategory(products, ['skylight', 'flashing'], 'Flashing');
    if (skylightProduct) {
      const unitPrice = getProductPrice(skylightProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: skylightProduct.id,
        description: skylightProduct.name,
        quantity: skylightCount,
        unit: 'each',
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: skylightCount * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${skylightCount} skylights`,
      });
    }
  }

  // 13. CHIMNEY FLASHING
  const chimneyCount = measurements.chimney_count || 0;
  if (chimneyCount > 0) {
    const chimneyProduct = findProductByCategory(products, ['chimney', 'flashing'], 'Flashing');
    if (chimneyProduct) {
      const unitPrice = getProductPrice(chimneyProduct.id, priceBookId, productPrices);
      lineItems.push({
        product_id: chimneyProduct.id,
        description: chimneyProduct.name,
        quantity: chimneyCount,
        unit: 'each',
        unit_price: unitPrice,
        discount_percent: 0,
        discount_amount: 0,
        line_total: chimneyCount * unitPrice,
        calculation_source: 'auto',
        calculation_note: `${chimneyCount} chimneys`,
      });
    }
  }

  return lineItems;
}
